// OWNED BY the meetings tab agent (created under detail/tabs/meetings/).
//
// Business logic for the customer-page פגישות tab, PORTED (logic only — no
// component reuse, and per the handbook nothing here imports from tabs/tasks/)
// from the legacy calendar surfaces:
//   - per-contact filtering: ContactTableNestedRowEvents.getContactEvents
//   - owner-only editing: CalendarTasksRow.isOwner
//   - meeting row anatomy (Groups icon, event-color stripe, attendee "+N",
//     listing address+price chip): CalendarTasksRow
//   - date captions: CalendarTasksRow's today-shows-time-only rule, Hebrew-ified
//
// Capability inventory vs the legacy surfaces (meetings domain):
//   PORTED — Groups icon for non-task events; event-color stripe; owner-only
//     row click; non-owner "(email)" hint; listing address (+ short price)
//     chip with "+N" for extra listings; attendee display; today → time-only
//     caption; allDay events show a date-only caption.
//   IMPROVED (legacy showed ONE listing) — a meeting linked to several
//     properties now renders as many address chips as the width allows
//     (breakpoint-driven, see MeetingListingChips.tsx) and the "+N" chip both
//     names the rest on hover and expands them in place on click. Legacy
//     CalendarTasksRow rendered listingsData[0] + a bare "+N" with no way to
//     learn what the N were.
//   PORTED (hardened) — attendee-phone matching: legacy compared ONLY
//     callerPhoneNumber; we also match contact.id (what parseContactsToAttendees
//     actually stores) and the Cloud-Function-stamped contactId field.
//   ADDED (legacy gap) — past meetings: getFilteredEventsByType hides meetings
//     from every tab except today/thisWeek/upcoming, so legacy surfaces never
//     show meeting history. The עבר group is new capability.
//   DROPPED — task checkbox / postpone-to-today (tasks-only affordances; the
//     משימות tab owns them); the first-attendee avatar (redundant here — every
//     meeting on this page is with this contact; we show OTHER attendees instead).

import { db, firebase } from "firebaseConfig";
import { differenceInDays, format, startOfDay } from "date-fns";
import { GenericConverter } from "utils/firestoreConverters";
import { formatShortCurrency, getFullName } from "utils/Haim/utils";
import { fetchCached } from "../../data/sessionCache";
import {
  CALENDAR_EVENTS_SUBCOLLECTION_NAME,
  CALENDAR_SETTINGS_COLLECTION_NAME
} from "../../../../../../@types/sharedSchema";
import type {
  CalendarEvent,
  ProcessedListingAddress,
  RealEstateContact
} from "../../../../../../@types/sharedSchema";

// ---------------------------------------------------------------------------
// Session-cache warm-up (fired from preloadMeetingsTab on contacts-row hover).
//
// The key string INTENTIONALLY equals the one the tasks tab uses
// (`tasks:calendar-events:<email>`): both tabs read the same whole-own-calendar
// snapshot (value type CalendarEvent[]), so sharing the key means ONE Firestore
// fetch warms both tabs (handbook: identical key ⇒ identical value type). Do
// not change one side without the other.
// ---------------------------------------------------------------------------

export const ownCalendarEventsCacheKey = (userEmail: string): string =>
  `tasks:calendar-events:${userEmail}`;

const fetchOwnCalendarEvents = async (userEmail: string): Promise<CalendarEvent[]> => {
  const snapshot = await db
    .collection(CALENDAR_SETTINGS_COLLECTION_NAME)
    .doc(userEmail)
    .collection(CALENDAR_EVENTS_SUBCOLLECTION_NAME)
    .withConverter(GenericConverter<CalendarEvent>())
    .get();

  return snapshot.docs.filter((doc) => doc.id != null).map((doc) => doc.data());
};

/** One-shot warm of the user's own calendar into the session cache. Error-safe. */
export function warmContactMeetingsCache(): void {
  const userEmail = firebase.auth().currentUser?.email;
  if (!userEmail) return;

  void fetchCached(ownCalendarEventsCacheKey(userEmail), () => fetchOwnCalendarEvents(userEmail), {
    maxAgeMs: 2 * 60_000
  }).catch(() => undefined);
}

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

/** Meetings are every NON-task event type — tolerant of legacy events missing eventType. */
export function isMeetingEvent(event: CalendarEvent): boolean {
  return event.eventType !== "task";
}

const contactPhonesOf = (contact: RealEstateContact): Set<string> =>
  new Set([contact.id, contact.contactId].filter(Boolean) as string[]);

/** Hardened per-contact matcher (see capability inventory above). */
export function isEventOfContact(event: CalendarEvent, contact: RealEstateContact): boolean {
  const contactPhones = contactPhonesOf(contact);

  if (event.contactId && contactPhones.has(event.contactId)) return true;

  return (event.attendees ?? []).some(
    ({ phoneNumberInternational }) =>
      !!phoneNumberInternational && contactPhones.has(phoneNumberInternational)
  );
}

/**
 * Owner guard (ported from CalendarTasksRow.isOwner), tolerant of the
 * Cloud-Function stamping latency: an own-calendar event without a userId
 * stamp yet is treated as own.
 */
export function isOwnCalendarEvent(event: CalendarEvent, userEmail?: string | null): boolean {
  return !event.userId || event.userId === userEmail;
}

// ---------------------------------------------------------------------------
// Grouping — הבאות (start day >= today, soonest first) / עבר (newest first)
// ---------------------------------------------------------------------------

export type ContactMeetingsSplit = {
  upcoming: CalendarEvent[];
  past: CalendarEvent[];
};

export function splitContactMeetings(meetings: CalendarEvent[]): ContactMeetingsSplit {
  const today = startOfDay(new Date());

  const upcoming: CalendarEvent[] = [];
  const past: CalendarEvent[] = [];

  for (const meeting of meetings) {
    if (startOfDay(new Date(meeting.start)) >= today) upcoming.push(meeting);
    else past.push(meeting);
  }

  const byStartAsc = (a: CalendarEvent, b: CalendarEvent): number =>
    new Date(a.start).getTime() - new Date(b.start).getTime();

  upcoming.sort(byStartAsc);
  past.sort((a, b) => byStartAsc(b, a));

  return { upcoming, past };
}

export function isMeetingToday(meeting: CalendarEvent): boolean {
  return differenceInDays(startOfDay(new Date(meeting.start)), startOfDay(new Date())) === 0;
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

const HEBREW_DAY_NAMES = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

/** "היום · 14:00", "מחר · כל היום", "חמישי 24.7 · 10:30", "אתמול · 09:00", "3.2.2025 · 16:00" */
export function formatMeetingWhenLabel(meeting: CalendarEvent): string {
  const start = new Date(meeting.start);
  const dayDiff = differenceInDays(startOfDay(start), startOfDay(new Date()));
  const timePart = meeting.allDay ? "כל היום" : format(start, "HH:mm");

  if (dayDiff === 0) return `היום · ${timePart}`;
  if (dayDiff === 1) return `מחר · ${timePart}`;
  if (dayDiff === -1) return `אתמול · ${timePart}`;
  if (dayDiff > 1 && dayDiff < 7) {
    return `${HEBREW_DAY_NAMES[start.getDay()]} ${format(start, "d.M")} · ${timePart}`;
  }
  if (dayDiff < 0) return `${format(start, "d.M.yyyy")} · ${timePart}`;

  const sameYear = start.getFullYear() === new Date().getFullYear();
  return `${format(start, sameYear ? "d.M" : "d.M.yyyy")} · ${timePart}`;
}

export type MeetingListingChip = {
  key: string;
  /** "רוטשילד 12, תל אביב" — the chip label when the row carries several listings. */
  address: string;
  /** " (2.5M₪)" or "" — appended only on a single-listing row (the legacy look). */
  priceSuffix: string;
  /** Address + price. Always the tooltip text, so a truncated chip never loses information. */
  full: string;
};

export type MeetingListings = {
  /** One entry per linked listing we can label, in the order stored on the event. */
  chips: MeetingListingChip[];
  /**
   * Linked listings we can NOT label: entries whose address resolves to nothing,
   * plus any `listingIds` with no matching `listingsData` entry (events written
   * outside CalendarForm may carry ids only). Counted into the "+N" so a row can
   * never under-report how many properties a meeting covers.
   */
  unlabeledCount: number;
};

/**
 * ALL listings linked to a meeting, ready to render (was: only the first one).
 *
 * No fetching, by design: `listingsData` is denormalized onto the event document
 * by CalendarForm (`listingsData: selectedListings`), so every linked listing —
 * address, price and all — already arrives with the event. The row therefore
 * costs zero extra reads no matter how many properties a meeting covers.
 */
export function meetingListingsOf(meeting: CalendarEvent): MeetingListings {
  const listings = meeting.listingsData ?? [];

  const chips: MeetingListingChip[] = [];
  let unlabeledCount = 0;

  listings.forEach((listing, index) => {
    // Legacy docs can miss `address` entirely — the type says otherwise.
    const listingAddress: Partial<ProcessedListingAddress> = listing.address ?? {};
    const { street, neighborhood, city } = listingAddress;

    const address = (street ? [street, city] : [neighborhood, city]).filter(Boolean).join(", ");

    if (!address) {
      unlabeledCount += 1;
      return;
    }

    const priceSuffix = listing.price ? ` (${formatShortCurrency(listing.price)})` : "";

    chips.push({
      key: listing.id ?? listing.originalListingId ?? `${address}-${index}`,
      address,
      priceSuffix,
      full: `${address}${priceSuffix}`
    });
  });

  // An id with no denormalized copy is still another property on this meeting.
  const idsWithoutData = (meeting.listingIds?.length ?? 0) - listings.length;

  return { chips, unlabeledCount: unlabeledCount + Math.max(idsWithoutData, 0) };
}

/** Names of attendees OTHER than this contact (the contact itself is implied). */
export function otherAttendeeNames(meeting: CalendarEvent, contact: RealEstateContact): string[] {
  const contactPhones = contactPhonesOf(contact);

  return (meeting.attendees ?? [])
    .filter(
      ({ phoneNumberInternational }) =>
        !phoneNumberInternational || !contactPhones.has(phoneNumberInternational)
    )
    .map(
      ({ firstName, lastName, phoneNumberInternational }) =>
        getFullName(firstName, lastName) || phoneNumberInternational || ""
    )
    .filter(Boolean);
}
